import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { environment } from "src/environments/environment";
import { AddressModel, SearchAddressModel } from "../models/address.model";
import { ApiResult } from "../models/api-result.model";
import { BindPolicyResultModel } from "../models/bind-policy-result.model";
import { CalculateCoverRequest } from "../models/calculate-cover-request";
import { CalculateCoverResponse } from "../models/calculate-cover-response";
import { CalculatePayoutRequest } from "../models/calculate-payment-request";
import { CalculatePaymentResponse } from "../models/calculate-payment-response";
import { ConditionModel } from "../models/condition.model";
import { LeadRequest } from "../models/lead-request";
import { LeadResult } from "../models/lead-result";
import { MagnumResultModel } from "../models/magnum-result.model";
import { MagnumUrlModel } from "../models/magnum-url.model";
import { OtpResponseModel } from "../models/otp-response.model";
import { PolicyDateInfoRequest } from "../models/policy-date-info-request";
import { QQOtpRequest, QQVerifyOtpRequest } from "../models/qq-otp-request";
import { QQOtpResponse, QQVerifyOtpResponse } from "../models/qq-otp-response";
import { QuestionnaireModel } from "../models/questionnaire.model";
import { QuickQuoteRequest } from "../models/quick-quote-request";
import { QuickQuoteResult } from "../models/quick-quote-result";
import { AnswerModel, QuoteModel } from "../models/quote.model";
import { SaveBankDetailsRequest } from "../models/save-bank-details-request";
import { ContactDateRequest } from "../models/save-contact-date-request";
import { GetAddressRequest, SearchAddressRequest } from "../models/search-address-request";
import { SiteContentModel } from "../models/site-content.model";
import { ValidationResult } from "../models/validation.result";
import { VerifyOtpResponse } from "../models/verify-otp.response";
import { checkStatus } from "../util/check-status";
import { delayPromise } from "../util/delay-promise";
import { ObAffordabilityModel } from "../models/ob-affordability.model";

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  constructor(private http: HttpClient) { }

  private httpGet<T>(path: string) {
    return this.http.get<ApiResult<T>>(environment.baseUrl + path)
      .pipe(checkStatus());
  }

  private httpGetAsync<T>(path: string) {
    return firstValueFrom(this.httpGet<T>(path));
  }

  private httpPost<T>(path: string, request: any) {
    return this.http.post<ApiResult<T>>(environment.baseUrl + path, request)
      .pipe(checkStatus());
  }

  private httpPostAsync<T>(path: string, request: any) {
    return firstValueFrom(this.httpPost<T>(path, request));
  }

  getSiteContent(name: string) {
    return this.httpGetAsync<SiteContentModel>(`/api/v1/siteContent/${name}`);
  }

  async getQuestionnaire(name: string) {
    for (var i = 0; i < 3; i++) {
      try {
        return await this.httpGetAsync<QuestionnaireModel>(`/api/v1/questionnaire/${name}`);
      } catch (e) {
        if (i == 2)
          throw e;
        await delayPromise(i * 200);
      }
    }
  }

  saveQuestionnaire(name: string, quoteKey: string, answers: { Key: string, Value: string }[], sourceUrl: string) {
    return this.httpPostAsync<QuoteModel>(`/api/v1/questionnaire/${name}/save`, { key: quoteKey, sourceUrl, answers });
  }

  comparativeQuotes(name: string, key: string, notifyClient: boolean, answers: AnswerModel[][]) {
    return this.httpPostAsync<QuickQuoteResult[]>(`/api/v1/questionnaire/${name}/comparativeQuotes`, {
      key,
      notifyClient,
      answers
    });
  }

  submitQuickQuote(name: string, request: QuickQuoteRequest) {
    return this.httpPostAsync<QuickQuoteResult>(`/api/v1/questionnaire/${name}/submit`, request);
  }

  submitLead(name: string, request: LeadRequest) {
    return this.httpPostAsync<LeadResult>(`/api/v1/questionnaire/${name}/lead`, request);
  }

  sendQuote(key: string) {
    return this.httpGetAsync(`/api/v1/questionnaire/notify/${key}`);
  }

  async sendOtpQuickQuote(request: QQOtpRequest) {
    return await this.httpPostAsync<QQOtpResponse>(`/api/v1/questionnaire/sendOtp`, request);
  }

  async verifyOtpQuickQuote(request: QQVerifyOtpRequest) {
    return await this.httpPostAsync<QQVerifyOtpResponse>(`/api/v1/questionnaire/verifyOtp`, request);
  }

  validate(questionId: string, value: string, answers: { Key: string, Value: string }[]) {
    return this.httpPostAsync<ValidationResult>('/api/v1/validate', { questionId, value, answers });
  }

  calculatePayout(request: CalculatePayoutRequest) {
    return this.httpPostAsync<CalculatePaymentResponse>(`/api/v1/multiclaim/calculate`, request);
  }

  getConditions() {
    return this.httpGet<ConditionModel[]>('/api/v1/multiclaim/conditions');
  }

  async getQuote(key: string, dob: string, lastName: string) {
    return await this.httpGetAsync<QuoteModel>(`/api/v1/quote/get/${key}?dateOfBirth=${dob}&lastName=${lastName}`);
  }

  async sendQuoteOtp(email: string, dateOfBirth: string, lastName: string) {
    return await this.httpPostAsync<OtpResponseModel>('/api/v1/quote/sendOtp', { email, dateOfBirth, lastName });
  }

  async verifyOtp(email: string, otp: string, key: string) {
    return await this.httpPostAsync<VerifyOtpResponse>(`/api/v1/quote/verifyOtp`, { email, otp, key });
  }

  async listQuotes(code: string, email: string, lastName: string, dateOfBirth: string, page: number, pageSize: number) {
    return await this.httpPostAsync<{ quotes: QuoteModel[], hasMore: boolean }>(
      '/api/v1/quote/list',
      { code, email, lastName, dateOfBirth, page, pageSize });
  }

  searchAddress(request: SearchAddressRequest) {
    return this.httpPostAsync<SearchAddressModel[]>(`/api/v1/address/search`, request);
  }

  getAddress(request: GetAddressRequest) {
    return this.httpPostAsync<AddressModel>(`/api/v1/address/get`, request);
  }

  async getMagnumPremium(key: string, sourceUrl?: string) {
    return await this.httpGetAsync<MagnumResultModel>(`/api/v1/quote/getMagnumPremium/${key}?sourceUrl=${sourceUrl}`);
  }

  async getMagnumUrl(key: string) {
    return await this.httpGetAsync<MagnumUrlModel>(`/api/v1/quote/getMagnumUrl/${key}`);
  }

  validateBank(key: string, answers: { Key: string, Value: string }[]) {
    return this.httpPostAsync<ValidationResult>(`/api/v1/validate/validateBank/${key}`, answers);
  }

  saveBankDetails(key: string, request: SaveBankDetailsRequest) {
    return this.httpPostAsync(`/api/v1/quote/saveBankDetails/${key}`, request);
  }

  savePolicyDateInfo(key: string, request: PolicyDateInfoRequest) {
    return this.httpPostAsync(`/api/v1/quote/SavePolicyDateInfo/${key}`, request);
  }

  saveAddress(key: string, address: AddressModel) {
    return this.httpPostAsync<AnswerModel[]>(`/api/v1/quote/saveAddress/${key}`, address);
  }

  bindPolicy(key: string) {
    return this.httpGetAsync<BindPolicyResultModel>(`/api/v1/quote/bindPolicy/${key}`);
  }

  calculateCover(request: CalculateCoverRequest) {
    return this.httpPostAsync<CalculateCoverResponse>('/api/v1/coverCalculator', request);
  }

  saveContactRequestDate(key: string, request: ContactDateRequest) {
    return this.httpPostAsync(`/api/v1/quote/SaveContactRequestDate/${key}`, request);
  }
}
